import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useEffect, useMemo, useRef } from 'react';

import BackendService from '../services/backendService';
import { Template } from '../services/TemplateService/Template.type';
import { NEW_TEMPLATE_PARAM } from '../utils/urlUtils';
import useAccessBadge from './useAccessBadge';
import useAutosaveTemplate from './useAutosaveTemplate';
import { DesignerActions, useDesignerContext } from './useDesignerState';

export const useRawTemplate = (
  templateId?: string | null,
  templateTenantId?: string,
): {
  template: Template | undefined;
  isFetchingTemplate: boolean;
  isError: boolean;
  error: Error | null;
} => {
  const templateCacheKey = ['template', templateId];
  const dispatch = useDesignerContext(context => context.dispatch);

  const { data: template, isFetching: isFetchingTemplate, isError, error } = useQuery<
    Template | undefined
  >({
    queryKey: templateCacheKey,
    queryFn: async _ => {
      if (templateId) {
        if (templateTenantId) {
          return BackendService.instance.blueprint.retrieveTemplateAuthenticated(
            templateId,
            templateTenantId,
          ) as Promise<Template | undefined>;
        }
        return BackendService.instance.blueprint.retrieveTemplate(templateId) as Promise<
          Template | undefined
        >;
      }
      return Promise.resolve(undefined);
    },
    enabled: Boolean(templateId) && templateId !== NEW_TEMPLATE_PARAM,
    staleTime: 1000 * 60 * 5,
    retry: (count: number, error: Error) => {
      // due to SSR fetching we dont want to retry on 403s and 404s
      const axiosError = error as AxiosError;
      if (
        axiosError.response?.status &&
        [403, 404].includes(axiosError.response.status)
      ) {
        return false;
      }

      // Default retry is 3 times
      if (count > 3) {
        return false;
      }
      return true;
    },
  });

  // Only set forceLoadingState to 'success' when a real fetch completes
  // (transitioning from fetching → not fetching), not on optimistic cache updates.
  const wasFetchingRef = useRef(false);
  useEffect(() => {
    if (isFetchingTemplate) {
      wasFetchingRef.current = true;
    } else if (wasFetchingRef.current && template) {
      wasFetchingRef.current = false;
      dispatch({
        type: DesignerActions.setForceLoadingState,
        payload: 'success',
      });
    }
  }, [template, isFetchingTemplate, dispatch]);

  return {
    template,
    isFetchingTemplate,
    isError,
    error,
  };
};

function useTemplate(templateId?: string | null, templateTenantId?: string) {
  const { accessBadge: userAccessBadge } = useAccessBadge();

  const { error, isError, isFetchingTemplate, template } = useRawTemplate(
    templateId,
    templateTenantId,
  );

  const isTemplateOwner = useMemo(
    () => template && userAccessBadge?.user.id === template?.creator.uid,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userAccessBadge, template, templateId],
  );

  const {
    autoSaveTemplate,
    isSavingTemplate,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  } = useAutosaveTemplate();

  return {
    template,
    isFetchingTemplate,
    autoSaveTemplate,
    isSavingTemplate,
    isError,
    error,
    isTemplateOwner,
    isSavingTemplateError,
    isSavingTemplateSuccess,
    isSavingTemplateIdle,
  };
}
export default useTemplate;
